import { useState, useRef, useEffect } from "react";
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import TicketSalesTable from '../../components/organization/TicketSalesTable';
import BursaryRequestTable from '../../components/organization/BursaryRequestTable';

// Icons
import { Search, Ticket, HandCoins } from "lucide-react";

// Shadcn Components
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

// Hooks
import { useBursaryRequests } from '../../hooks/organization/useBursaryRequests';
import { useTransactions } from '../../hooks/organization/useTransactions';

function OrganizationTransactions() {
    const uuid = localStorage.getItem('tenantId');
    const [activeTab, setActiveTab] = useState<string>('transactions');
    const eventDropdownRef = useRef<HTMLDivElement>(null);
    const bursaryEventDropdownRef = useRef<HTMLDivElement>(null);

    const {
        isLoading: bursaryLoading,
        error: bursaryError,
        selectedBursary,
        showBursaryModal,
        showName,
        showModalName,
        showModalEmail,
        statusFilters,
        selectedBursaryEvent,
        showBursaryEventDropdown,
        filteredBursaryRequests,
        uniqueEvents: bursaryEvents,
        setShowBursaryModal,
        setSelectedBursaryEvent,
        setShowBursaryEventDropdown,
        handleAccept,
        handleReject,
        toggleNameVisibility,
        toggleModalNameVisibility,
        toggleModalEmailVisibility,
        handleViewDetails,
        toggleStatusFilter,
        setError: setBursaryError
    } = useBursaryRequests(uuid);

    const {
        isLoading: transactionsLoading,
        error: transactionsError,
        searchQuery,
        selectedEvent,
        showEventDropdown,
        uniqueEvents,
        filteredTransactions,
        setSearchQuery,
        setSelectedEvent,
        setShowEventDropdown
    } = useTransactions(uuid, activeTab);

    // Handle click outside for event dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (eventDropdownRef.current && !eventDropdownRef.current.contains(event.target as Node)) {
                setShowEventDropdown(false);
            }
            if (bursaryEventDropdownRef.current && !bursaryEventDropdownRef.current.contains(event.target as Node)) {
                setShowBursaryEventDropdown(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <OrganizationLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-3xl font-bold tracking-tight">Transactions</h2>
                        </div>

                <Tabs defaultValue="transactions" className="space-y-4" onValueChange={setActiveTab}>
                    <TabsList className="transition-none">
                        <TabsTrigger value="transactions" className="flex items-center gap-2 transition-none">
                            <Ticket className="h-4 w-4" />
                            Ticket Sales
                        </TabsTrigger>
                        <TabsTrigger value="bursary" className="flex items-center gap-2 transition-none">
                            <HandCoins className="h-4 w-4" />
                            Bursary Requests
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="transactions" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Ticket Sales</CardTitle>
                            </CardHeader>
                            <CardContent>
                            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
                                <div className="relative w-full md:w-1/3">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input 
                                        type="text" 
                                        placeholder="Search by customer name..." 
                                        value={searchQuery} 
                                            onChange={(e) => setSearchQuery(e.target.value)} 
                                            className="pl-9"
                                    />
                                </div>
                                <div className="relative w-full md:w-64" ref={eventDropdownRef}>
                                        <Button
                                            variant="outline"
                                        onClick={() => setShowEventDropdown(!showEventDropdown)}
                                            className="w-full justify-between transition-none"
                                    >
                                            <span className="truncate">
                                                {uniqueEvents.find(([uuid]) => uuid === selectedEvent)?.[1] || 'Select Event'}
                                            </span>
                                            <svg
                                                className={cn(
                                                    "h-4 w-4 text-muted-foreground transition-transform duration-100",
                                                    showEventDropdown && "transform rotate-180"
                                                )}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </Button>
                                    {showEventDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md transition-all duration-100">
                                            <div className="max-h-60 overflow-y-auto">
                                                {uniqueEvents.map(([uuid, title]) => (
                                                        <Button
                                                        key={uuid}
                                                            variant="ghost"
                                                        onClick={() => {
                                                            setSelectedEvent(uuid);
                                                            setShowEventDropdown(false);
                                                        }}
                                                            className={cn(
                                                                "w-full justify-start font-normal",
                                                                selectedEvent === uuid && "bg-accent"
                                                            )}
                                                    >
                                                        {title}
                                                        </Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                                <TicketSalesTable 
                                    transactions={filteredTransactions}
                                    isLoading={transactionsLoading}
                                    error={transactionsError}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="bursary" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Bursary Requests</CardTitle>
                            </CardHeader>
                            <CardContent>
                                    <div className="mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                                        <div className="flex flex-wrap gap-1.5">
                                        <Button
                                            variant={statusFilters.pending ? "default" : "outline"}
                                                onClick={() => toggleStatusFilter('pending')}
                                            className={cn(
                                                "rounded-full text-xs px-3 h-8",
                                                statusFilters.pending && "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                                            )}
                                            >
                                                Pending
                                        </Button>
                                        <Button
                                            variant={statusFilters.approved ? "default" : "outline"}
                                                onClick={() => toggleStatusFilter('approved')}
                                            className={cn(
                                                "rounded-full text-xs px-3 h-8",
                                                statusFilters.approved && "bg-green-100 text-green-800 hover:bg-green-200"
                                            )}
                                            >
                                                Approved
                                        </Button>
                                        <Button
                                            variant={statusFilters.rejected ? "default" : "outline"}
                                                onClick={() => toggleStatusFilter('rejected')}
                                            className={cn(
                                                "rounded-full text-xs px-3 h-8",
                                                statusFilters.rejected && "bg-red-100 text-red-800 hover:bg-red-200"
                                            )}
                                            >
                                                Rejected
                                        </Button>
                                        </div>

                                        <div className="relative w-full md:w-64" ref={bursaryEventDropdownRef}>
                                        <Button
                                            variant="outline"
                                                onClick={() => setShowBursaryEventDropdown(!showBursaryEventDropdown)}
                                            className="w-full justify-between transition-none"
                                            >
                                                    <span className="truncate">
                                                {bursaryEvents.find(([uuid]) => uuid === selectedBursaryEvent)?.[1] || 'Select Event'}
                                                    </span>
                                                    <svg
                                                className={cn(
                                                    "h-4 w-4 text-muted-foreground transition-transform duration-100",
                                                    showBursaryEventDropdown && "transform rotate-180"
                                                )}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                        </Button>
                                            {showBursaryEventDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md transition-all duration-100">
                                                    <div className="max-h-60 overflow-y-auto">
                                                    {bursaryEvents.map(([uuid, title]) => (
                                                        <Button
                                                                key={uuid}
                                                            variant="ghost"
                                                                onClick={() => {
                                                                    setSelectedBursaryEvent(uuid);
                                                                    setShowBursaryEventDropdown(false);
                                                                }}
                                                            className={cn(
                                                                "w-full justify-start font-normal",
                                                                selectedBursaryEvent === uuid && "bg-accent"
                                                            )}
                                                        >
                                                            {title}
                                                        </Button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <BursaryRequestTable 
                                    requests={filteredBursaryRequests}
                                    isLoading={bursaryLoading}
                                    error={bursaryError}
                                    showName={showName}
                                    showModalName={showModalName}
                                    showModalEmail={showModalEmail}
                                    showBursaryModal={showBursaryModal}
                                    selectedBursary={selectedBursary}
                                    onToggleNameVisibility={toggleNameVisibility}
                                    onToggleModalNameVisibility={toggleModalNameVisibility}
                                    onToggleModalEmailVisibility={toggleModalEmailVisibility}
                                    onViewDetails={handleViewDetails}
                                    onAccept={handleAccept}
                                    onReject={handleReject}
                                    onCloseModal={() => setShowBursaryModal(false)}
                                    onError={setBursaryError}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
                <div className="h-8" />
            </div>
        </OrganizationLayout>
    );
}

export default OrganizationTransactions;
